import {postGraphql} from "./ExternalAuthWebHelper";

export type McpApiKeyKind = "SUPPORT" | "ONIX_SERVER";

export interface McpApiKey {
    id: number;
    kind: McpApiKeyKind;
    name: string;
    keyPrefix: string;
    permissions: string[];
    createdAt: string;
    expiresAt?: string | null;
    lastUsedAt?: string | null;
    revokedAt?: string | null;
    isActive: boolean;
}

export interface McpApiKeyEntitlements {
    canCreateSupportKey: boolean;
    canCreateOnixServerKey: boolean;
    supportPermissions: string[];
    onixServerPermissions: string[];
}

export interface CreateMcpApiKeyInput {
    kind: McpApiKeyKind;
    name: string;
    expiresInDays: number | null;
    permissions: string[];
}

const keyFields = `
    id
    kind
    name
    keyPrefix
    permissions
    createdAt
    expiresAt
    lastUsedAt
    revokedAt
    isActive
`;

export const mcpApiKeyKindTitle = (kind: McpApiKeyKind): string =>
    kind === "SUPPORT" ? "Onix Support (Grunf)" : "OnixServer API";

export const mcpApiKeyHeader = (kind: McpApiKeyKind): string =>
    kind === "SUPPORT" ? "X-Support-MCP-Api-Key" : "X-Onix-Server-MCP-Api-Key";

export const mcpApiKeyEndpointPath = (kind: McpApiKeyKind): string =>
    kind === "SUPPORT" ? "/api/mcp/support" : "/api/mcp/onix-server";

export const mcpApiKeyPermissionTitles: Record<string, string> = {
    CanWriteInternalNotes: "Interne AI bilješke na tiketima",
    CanWriteTimeLogs: "Evidencija vremena (AI time log)",
    CanWriteCodexActivity: "Bilježenje Codex aktivnosti",
    CanExecuteDebugSql: "Debug SQL na bazi tvrtke (uz rollback)",
    CanReadRuntimeLogs: "Čitanje runtime logova baze tvrtke",
    CanManagePantheonDocs: "Uređivanje Pantheon dokumentacije",
    CanManageCompanyDbPantheonUsers: "Kreiranje Pantheon korisnika u bazi tvrtke",
    CanManageSupportAiKnowledge: "Uređivanje AI znanja tvrtke",
    CanAccessAllCompanySettings: "Pristup svim tenantima",
    CanManageOnixPhoneUserTasks: "OnixPhone obavijesti o održavanju",
    CanManageOnixPhoneUsers: "Kreiranje OnixPhone korisnika",
};

export const loadMcpApiKeys = async (): Promise<{
    keys: McpApiKey[];
    entitlements: McpApiKeyEntitlements;
}> => {
    const data = await postGraphql<{
        myMcpApiKeys: McpApiKey[];
        myMcpApiKeyEntitlements: McpApiKeyEntitlements;
    }>(`
        query MyMcpApiKeys {
            myMcpApiKeys {
                ${keyFields}
            }
            myMcpApiKeyEntitlements {
                canCreateSupportKey
                canCreateOnixServerKey
                supportPermissions
                onixServerPermissions
            }
        }
    `);

    return {keys: data.myMcpApiKeys, entitlements: data.myMcpApiKeyEntitlements};
};

export const createMcpApiKey = async (
    input: CreateMcpApiKeyInput,
): Promise<{key: string; apiKey: McpApiKey}> => {
    const data = await postGraphql<{
        createMcpApiKey: {key: string; apiKey: McpApiKey};
    }>(
        `
        mutation CreateMcpApiKey($apiKey: CreateMcpApiKeyDtoInput!) {
            createMcpApiKey(apiKey: $apiKey) {
                key
                apiKey {
                    ${keyFields}
                }
            }
        }
    `,
        {apiKey: input},
    );

    return data.createMcpApiKey;
};

export const revokeMcpApiKey = async (id: number): Promise<boolean> => {
    const data = await postGraphql<{revokeMcpApiKey: boolean}>(
        `
        mutation RevokeMcpApiKey($id: Int!) {
            revokeMcpApiKey(id: $id)
        }
    `,
        {id},
    );

    return data.revokeMcpApiKey;
};
